import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Alert,
  Box,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  LinearProgress,
  Grid,
} from "@mui/material";
import { Editor } from "@monaco-editor/react";
import { LoadingButton } from "@mui/lab";
import PlayArrowIcon from "@mui/icons-material/PlayArrow";
import { useThemeMode } from "context/ThemeContext";

interface PromptEditorModalProps {
  open: boolean;
  onClose: () => void;
  prompt: {
    _id: string;
    name: string;
    prompt: string;
    type: "system" | "user" | "chat";
    description: string;
  } | null;

  handlePromptChange: (value: string) => void;
  testEvaluationStatus: string | null;
  handleRunPromptTests: () => void;
  handleSavePromptEdit: () => void;
  testEvaluationData: any;
  isRunningTests: boolean;
}

const PromptEditorModal: React.FC<PromptEditorModalProps> = ({
  open,
  onClose,
  prompt,
  handlePromptChange,
  testEvaluationStatus,
  handleRunPromptTests,
  handleSavePromptEdit,
  testEvaluationData,
  isRunningTests,
}) => {
  const { mode } = useThemeMode();
  const handleClosePromptTestEditor = () => {
    onClose();
  };

  const handleSaveAndRunTests = () => {
    handleSavePromptEdit();
    handleRunPromptTests();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClosePromptTestEditor}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { height: "80vh", display: "flex", flexDirection: "column" },
      }}
    >
      <DialogTitle>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Typography variant="h6">Test Prompt Editor</Typography>
          <Typography variant="subtitle2" color="text.secondary">
            Press Ctrl+S to save and run tests
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <Box sx={{ mb: 2 }}>
          <Alert severity="info">
            Edit the prompt below and click "Execute All Tests" to run tests
            across all prompt datasets simultaneously. This provides
            comprehensive validation of your prompt across all test cases and
            providers. You can also press Ctrl+S as a shortcut to execute tests.
          </Alert>
        </Box>

        <Box
          sx={{
            flex: 1,
            border: 1,
            borderColor: "divider",
            borderRadius: 1,
            overflow: "hidden",
          }}
        >
          <Editor
            language="markdown"
            value={prompt?.prompt || ""}
            onChange={(value) => handlePromptChange(value || "")}
            onMount={(editor, monaco) => {
              editor.addCommand(
                monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS,
                () => {
                  handleRunPromptTests();
                },
              );
            }}
            theme={mode === "dark" ? "vs-dark" : "light"}
            options={{
              minimap: { enabled: false },
              lineNumbers: "on",
              scrollBeyondLastLine: false,
              automaticLayout: true,
            }}
          />
        </Box>

        {prompt && testEvaluationStatus === "completed" && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Test Results
            </Typography>
            <Paper variant="outlined" sx={{ p: 2 }}>
              {testEvaluationData.results &&
                testEvaluationData.results.results && (
                  <>
                    <Grid container spacing={2} sx={{ mb: 2 }}>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h5" color="success.main">
                            {
                              testEvaluationData.results.results.filter(
                                (r: any) => r.success,
                              ).length
                            }
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Tests Passed
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h5" color="error.main">
                            {
                              testEvaluationData.results.results.filter(
                                (r: any) => !r.success,
                              ).length
                            }
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Tests Failed
                          </Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={4}>
                        <Box sx={{ textAlign: "center" }}>
                          <Typography variant="h5">
                            {testEvaluationData.results.results.length}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Total Tests
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>

                    {testEvaluationData.results.results.filter(
                      (r: any) => !r.success,
                    ).length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" gutterBottom>
                          Failed Tests:
                        </Typography>
                        <List dense>
                          {testEvaluationData.results.results
                            .filter((r: any) => !r.success)
                            .map((result: any, index: number) => (
                              <ListItem key={index}>
                                <ListItemText
                                  primary={`Test ${index + 1}: ${result.error || "Failed assertion"}`}
                                  secondary={`Prompt Vars: ${JSON.stringify(result.vars)}`}
                                />
                              </ListItem>
                            ))}
                        </List>
                      </Box>
                    )}
                  </>
                )}
            </Paper>
          </Box>
        )}

        {isRunningTests && (
          <Box sx={{ width: "100%", mt: 2 }}>
            <LinearProgress />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Running tests...
            </Typography>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClosePromptTestEditor}>Close</Button>
        <LoadingButton
          onClick={handleRunPromptTests}
          loading={isRunningTests}
          loadingPosition="start"
          startIcon={<PlayArrowIcon />}
          variant="contained"
          color="primary"
        >
          Execute All Tests
        </LoadingButton>
        <LoadingButton
          onClick={handleSaveAndRunTests}
          loading={isRunningTests}
          variant="contained"
          color="primary"
        >
          Save Changes & Execute Tests
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default PromptEditorModal;
